import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

interface KitchenStats {
  central?: number;
  small?: number;
  active?: number;
  maintenance?: number;
  offline?: number;
  totalStations?: number;
  availableStations?: number;
}

interface Kitchen {
  id: string | number;
  name: string;
  type: string;
  status: string;
  schools?: number;
  pupils?: number;
  capacity?: number;
  utilization?: number;
  details?: string;
}

type StatVariant = 'central' | 'small' | 'maintenance' | 'offline' | undefined;

const KitchenNetworkOverview: React.FC = () => {
  const [kitchenStats, setKitchenStats] = useState<KitchenStats | null>(null);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const [statsResp, kitchensResp, stationsResp, availableResp] = await Promise.allSettled([
        fetch(`${API_BASE}/kitchen-ops/stats`, { headers }),
        fetch(`${API_BASE}/kitchen-ops/kitchens`, { headers }),
        fetch(`${API_BASE}/kitchen-ops/kitchen-stations`, { headers }),
        fetch(`${API_BASE}/kitchen-ops/kitchen-stations/available`, { headers }),
      ]);

      let statsData: KitchenStats = {};

      if (statsResp.status === 'fulfilled' && statsResp.value.ok) {
        const data = await statsResp.value.json();
        statsData = { ...statsData, ...(data.data ?? data) };
      }

      if (stationsResp.status === 'fulfilled' && stationsResp.value.ok) {
        const data = await stationsResp.value.json();
        const list = data.data ?? data;
        const totalStations = Array.isArray(list)
          ? list.length
          : (data.total ?? data.count ?? undefined);
        if (totalStations != null) statsData = { ...statsData, totalStations };
      }

      if (availableResp.status === 'fulfilled' && availableResp.value.ok) {
        const data = await availableResp.value.json();
        const list = data.data ?? data;
        const availableStations = Array.isArray(list)
          ? list.length
          : (data.total ?? data.count ?? undefined);
        if (availableStations != null) statsData = { ...statsData, availableStations };
      }

      if (Object.keys(statsData).length > 0) {
        setKitchenStats(statsData);
      }

      if (kitchensResp.status === 'fulfilled' && kitchensResp.value.ok) {
        const data = await kitchensResp.value.json();
        const list = data.data ?? data;
        setKitchens(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      setError((e as Error).message || 'Failed to load kitchen data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const getStatusBadgeStyles = (status: string): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      textTransform: 'uppercase',
    };
    switch (status) {
      case 'active':
        return { ...baseStyles, backgroundColor: '#d4edda', color: '#155724' };
      case 'maintenance':
        return { ...baseStyles, backgroundColor: '#fff3cd', color: '#856404' };
      case 'offline':
        return { ...baseStyles, backgroundColor: '#f8d7da', color: '#721c24' };
      default:
        return baseStyles;
    }
  };

  const getKitchenItemStyles = (type: string, status: string) => {
    let borderColor = '#28a745';
    if (type === 'central') borderColor = '#007bff';
    if (status === 'maintenance') borderColor = '#ffc107';
    if (status === 'offline') borderColor = '#dc3545';
    return {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f8f9fa',
      mb: 1.5,
      p: 2,
      borderRadius: 2,
      borderLeft: `4px solid ${borderColor}`,
    };
  };

  const getStatCardStyles = (variant?: StatVariant) => {
    let borderColor = '#28a745';
    if (variant === 'central') borderColor = '#007bff';
    if (variant === 'small') borderColor = '#17a2b8';
    if (variant === 'maintenance') borderColor = '#ffc107';
    if (variant === 'offline') borderColor = '#dc3545';
    return {
      backgroundColor: '#f8f9fa',
      p: 2,
      borderRadius: 2,
      textAlign: 'center',
      borderLeft: `4px solid ${borderColor}`,
    };
  };

  return (
    <Card sx={{ height: '100%', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', borderRadius: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Kitchen Network Overview
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Kitchen Statistics Grid */}
            {kitchenStats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4}>
                  <Box sx={getStatCardStyles('central')}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {kitchenStats.central ?? '--'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Central Kitchens</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={getStatCardStyles('small')}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {kitchenStats.small ?? '--'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Small Kitchens</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={getStatCardStyles()}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {kitchenStats.active ?? '--'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Active Today</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={getStatCardStyles('maintenance')}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {kitchenStats.maintenance ?? '--'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Maintenance</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={getStatCardStyles('offline')}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {kitchenStats.offline ?? '--'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Offline</Typography>
                  </Box>
                </Grid>
                {kitchenStats.totalStations != null && (
                  <Grid item xs={6} sm={4}>
                    <Box sx={getStatCardStyles()}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                        {kitchenStats.totalStations.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Stations</Typography>
                    </Box>
                  </Grid>
                )}
                {kitchenStats.availableStations != null && (
                  <Grid item xs={6} sm={4}>
                    <Box sx={getStatCardStyles()}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                        {kitchenStats.availableStations.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Available Stations</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Kitchen Status List */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
              Kitchen Status
            </Typography>
            {kitchens.length === 0 ? (
              <Alert severity="info">No kitchen data available from the API.</Alert>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {kitchens.map((kitchen) => (
                  <Box key={kitchen.id} sx={getKitchenItemStyles(kitchen.type, kitchen.status)}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {kitchen.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {kitchen.schools != null ? `Serves ${kitchen.schools} schools` : ''}
                        {kitchen.pupils != null ? ` • ${kitchen.pupils.toLocaleString()} pupils` : ''}
                        {kitchen.capacity != null ? ` • Capacity: ${(kitchen.capacity / 1000).toFixed(0)}k meals/day` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Box sx={getStatusBadgeStyles(kitchen.status)}>
                        {kitchen.status}
                      </Box>
                      <Typography variant="caption">
                        {kitchen.status === 'active'
                          ? `${kitchen.utilization ?? 0}% capacity`
                          : kitchen.details ?? ''}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default KitchenNetworkOverview;
